import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useDREReport } from "@/hooks/useDREReport";
import { useDREMonthlyData } from "@/hooks/useDREMonthlyData";
import { DRETable } from "@/components/reports/DRETable";
import { DRESummaryCards } from "@/components/reports/DRESummaryCards";
import { NetProfitEvolutionChart } from "@/components/reports/NetProfitEvolutionChart";
import { ExpenseCompositionChart } from "@/components/reports/ExpenseCompositionChart";
import { CMOChart } from "@/components/reports/CMOChart";
import { CMVDetailChart } from "@/components/reports/CMVDetailChart";
import { FileSpreadsheet, Download, BarChart3, Table } from "lucide-react";
import { format, startOfMonth, endOfMonth, subMonths } from "date-fns";

type DatePreset = "current_month" | "last_month" | "last_3_months" | "custom";

function getPresetDates(preset: DatePreset): { from: string; to: string } {
  const today = new Date();

  switch (preset) {
    case "current_month":
      return {
        from: format(startOfMonth(today), "yyyy-MM-dd"),
        to: format(endOfMonth(today), "yyyy-MM-dd"),
      };
    case "last_month": {
      const lastMonth = subMonths(today, 1);
      return {
        from: format(startOfMonth(lastMonth), "yyyy-MM-dd"),
        to: format(endOfMonth(lastMonth), "yyyy-MM-dd"),
      };
    }
    case "last_3_months": {
      const threeMonthsAgo = subMonths(today, 3);
      return {
        from: format(startOfMonth(threeMonthsAgo), "yyyy-MM-dd"),
        to: format(endOfMonth(today), "yyyy-MM-dd"),
      };
    }
    default:
      return {
        from: format(startOfMonth(today), "yyyy-MM-dd"),
        to: format(endOfMonth(today), "yyyy-MM-dd"),
      };
  }
}

export default function DREPage() {
  const [datePreset, setDatePreset] = useState<DatePreset>("current_month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  const { from: dateFrom, to: dateTo } = useMemo(() => {
    if (datePreset === "custom" && customFrom && customTo) {
      return { from: customFrom, to: customTo };
    }
    return getPresetDates(datePreset);
  }, [datePreset, customFrom, customTo]);

  const { data, isLoading, error } = useDREReport({ dateFrom, dateTo });
  const { data: chartsData, isLoading: chartsLoading } = useDREMonthlyData({ 
    months: 6,
    dateFrom,
    dateTo 
  });

  const handlePresetChange = (value: DatePreset) => {
    setDatePreset(value);
    if (value !== "custom") {
      setCustomFrom("");
      setCustomTo("");
    }
  };

  const handleExportPDF = () => {
    // TODO: Implement PDF export
    console.log("Export PDF", data);
  };

  const periodLabel = useMemo(() => {
    const fromDate = new Date(dateFrom + "T12:00:00");
    const toDate = new Date(dateTo + "T12:00:00");
    return `${format(fromDate, "dd/MM/yyyy")} a ${format(toDate, "dd/MM/yyyy")}`;
  }, [dateFrom, dateTo]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileSpreadsheet className="h-6 w-6" />
            DRE - Demonstrativo de Resultado
          </h1>
          <p className="text-muted-foreground">
            Análise de receitas, custos e despesas do período
          </p>
        </div>

        <Button onClick={handleExportPDF} variant="outline" disabled={!data}>
          <Download className="mr-2 h-4 w-4" />
          Exportar PDF
        </Button>
      </div>

      {/* Date filters */}
      <div className="flex flex-wrap items-end gap-4 p-4 bg-muted/50 rounded-lg">
        <div className="space-y-2">
          <Label>Período</Label>
          <Select value={datePreset} onValueChange={(v) => handlePresetChange(v as DatePreset)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="current_month">Mês Atual</SelectItem>
              <SelectItem value="last_month">Mês Anterior</SelectItem>
              <SelectItem value="last_3_months">Últimos 3 Meses</SelectItem>
              <SelectItem value="custom">Personalizado</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {datePreset === "custom" && (
          <>
            <div className="space-y-2">
              <Label>De</Label>
              <Input
                type="date"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="w-[160px]"
              />
            </div>
            <div className="space-y-2">
              <Label>Até</Label>
              <Input
                type="date"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="w-[160px]"
              />
            </div>
          </>
        )}

        <div className="text-sm text-muted-foreground">
          Período: <span className="font-medium">{periodLabel}</span>
        </div>
      </div>

      {/* Content */}
      {isLoading || chartsLoading ? (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-[100px]" />
            ))}
          </div>
          <Skeleton className="h-[500px]" />
        </div>
      ) : error ? (
        <div className="p-8 text-center text-destructive">
          Erro ao carregar o DRE. Tente novamente.
        </div>
      ) : data ? (
        <div className="space-y-6">
          <DRESummaryCards data={data} />
          
          <Tabs defaultValue="table" className="w-full">
            <TabsList>
              <TabsTrigger value="table" className="flex items-center gap-2">
                <Table className="h-4 w-4" />
                Tabela DRE
              </TabsTrigger>
              <TabsTrigger value="charts" className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Gráficos
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="table" className="mt-4">
              <DRETable data={data} />
            </TabsContent>
            
            <TabsContent value="charts" className="mt-4 space-y-6">
              {/* Evolution Chart */}
              <NetProfitEvolutionChart data={chartsData?.monthlyData || []} />
              
              {/* Cost Analysis Row */}
              <div className="grid gap-6 md:grid-cols-2">
                <CMVDetailChart 
                  data={chartsData?.cmvDetail || []} 
                  total={chartsData?.cmvTotal || 0}
                  netRevenue={data.netRevenue}
                />
                <CMOChart 
                  data={chartsData?.cmoDetail || []} 
                  total={chartsData?.cmoTotal || 0}
                  netRevenue={data.netRevenue}
                />
              </div>
              
              {/* Expense Composition */}
              <ExpenseCompositionChart 
                data={chartsData?.expenseComposition || []} 
                total={chartsData?.expenseTotal || 0}
              />
            </TabsContent>
          </Tabs>
        </div>
      ) : (
        <div className="p-8 text-center text-muted-foreground">
          Selecione um período para visualizar o DRE.
        </div>
      )}
    </div>
  );
}
